"use client";

import { useEffect, useId, useRef, useState } from "react";
import { formatWorkspaceDate } from "@/lib/display-preferences";
import { useDialogFocus } from "./use-dialog-focus";
import "./core-history.css";

export type CoreHistoryItem = {
  id: string;
  title: string;
  preview: string;
  updatedAt: string;
  messageCount: number;
  status: "empty" | "running" | "complete" | "stopped" | "error";
};

export type CoreHistoryProps = {
  items: CoreHistoryItem[];
  loading: boolean;
  error?: string | null;
  query: string;
  onQueryChange: (query: string) => void;
  onRetry: () => void | Promise<unknown>;
  onLoadMore: () => void | Promise<unknown>;
  hasMore: boolean;
  busyId?: string | null;
  currentId?: string | null;
  onOpen: (id: string) => Promise<boolean | void>;
  onRename: (id: string, title: string) => Promise<boolean>;
  onRemove: (id: string) => Promise<boolean>;
};

function updatedLabel(value: string) {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Saved chat";
  return formatWorkspaceDate(date, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function stateLabel(item: CoreHistoryItem) {
  if (item.status === "running") return "In progress";
  if (item.status === "stopped") return "Stopped";
  if (item.status === "error") return "Needs attention";
  return item.messageCount === 1 ? "1 message" : `${item.messageCount} messages`;
}

/** The History view stays within Core; the parent owns durable data and search. */
export default function CoreHistory({ items, loading, error, query, onQueryChange, onRetry, onLoadMore, hasMore, busyId, currentId, onOpen, onRename, onRemove }: CoreHistoryProps) {
  const headingId = useId();
  const searchId = useId();
  const renameErrorId = useId();
  const deleteTitleId = useId();
  const deleteDescriptionId = useId();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CoreHistoryItem | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pending, setPending] = useState<{ id: string; kind: "open" | "rename" | "delete" } | null>(null);
  const [announcement, setAnnouncement] = useState("");
  const pendingRef = useRef(false);
  const searchInput = useRef<HTMLInputElement>(null);
  const renameInput = useRef<HTMLInputElement>(null);
  const deletePanel = useRef<HTMLElement>(null);
  const renameButtons = useRef(new Map<string, HTMLButtonElement>());
  const locked = Boolean(busyId || pending);

  useEffect(() => {
    if (!editingId) return;
    renameInput.current?.focus({ preventScroll: true });
    renameInput.current?.select();
    function cancelOnEscape(event: KeyboardEvent) {
      if (event.key !== "Escape" || pendingRef.current) return;
      event.preventDefault();
      setEditingId(null);
      setRenameError(null);
      requestAnimationFrame(() => (renameButtons.current.get(editingId!) ?? searchInput.current)?.focus({ preventScroll: true }));
    }
    document.addEventListener("keydown", cancelOnEscape);
    return () => document.removeEventListener("keydown", cancelOnEscape);
  }, [editingId]);

  function closeDelete() {
    if (pendingRef.current) return;
    setDeleteTarget(null);
    setDeleteError(null);
  }
  useDialogFocus(deleteTarget !== null, deletePanel, closeDelete);

  function stopRename() {
    if (pendingRef.current) return;
    const renamedId = editingId;
    setEditingId(null);
    setRenameError(null);
    requestAnimationFrame(() => (renameButtons.current.get(renamedId ?? "") ?? searchInput.current)?.focus({ preventScroll: true }));
  }

  async function openChat(item: CoreHistoryItem) {
    if (pendingRef.current || busyId || editingId) return;
    pendingRef.current = true;
    setPending({ id: item.id, kind: "open" });
    setActionError(null);
    try {
      if (await onOpen(item.id) === false) setActionError("This chat could not be opened. Try again.");
    } catch (cause) {
      setActionError(cause instanceof Error ? cause.message : "This chat could not be opened. Try again.");
    } finally {
      pendingRef.current = false;
      setPending(null);
    }
  }

  async function renameChat(event: React.FormEvent<HTMLFormElement>, item: CoreHistoryItem) {
    event.preventDefault();
    if (pendingRef.current || busyId) return;
    const title = draftTitle.trim();
    if (!title) { setRenameError("Enter a name for this chat."); renameInput.current?.focus(); return; }
    if (title === item.title) { stopRename(); return; }
    pendingRef.current = true;
    setPending({ id: item.id, kind: "rename" });
    setRenameError(null);
    try {
      if (!await onRename(item.id, title)) { setRenameError("The name could not be saved. Your changes are still here. Try again."); return; }
      setEditingId(null);
      setAnnouncement("Chat renamed.");
      requestAnimationFrame(() => (renameButtons.current.get(item.id) ?? searchInput.current)?.focus({ preventScroll: true }));
    } catch (cause) {
      setRenameError(cause instanceof Error ? cause.message : "The name could not be saved. Try again.");
    } finally {
      pendingRef.current = false;
      setPending(null);
    }
  }

  async function removeChat() {
    if (!deleteTarget || pendingRef.current || busyId) return;
    const target = deleteTarget;
    pendingRef.current = true;
    setPending({ id: target.id, kind: "delete" });
    setDeleteError(null);
    try {
      if (!await onRemove(target.id)) { setDeleteError("This chat could not be deleted. Try again."); return; }
      setDeleteTarget(null);
      setAnnouncement("Chat deleted.");
      requestAnimationFrame(() => searchInput.current?.focus({ preventScroll: true }));
    } catch (cause) {
      setDeleteError(cause instanceof Error ? cause.message : "This chat could not be deleted. Try again.");
    } finally {
      pendingRef.current = false;
      setPending(null);
    }
  }

  return <section className="core-history" aria-labelledby={headingId}>
    <header className="core-history-heading"><h2 id={headingId}>Chats</h2><p>Pick up where you left off.</p></header>
    <div className="core-history-search">
      <label htmlFor={searchId}>Search chats</label>
      <div><svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="10.5" cy="10.5" r="6.5"/><path d="m16 16 4.5 4.5"/></svg><input ref={searchInput} id={searchId} type="search" value={query} onChange={event => { setActionError(null); onQueryChange(event.target.value); }} placeholder="Search titles and messages" autoComplete="off" disabled={Boolean(editingId || deleteTarget || pending)} />{query && <button type="button" onClick={() => { onQueryChange(""); searchInput.current?.focus(); }} disabled={Boolean(editingId || deleteTarget || pending)} aria-label="Clear chat search">Clear</button>}</div>
    </div>
    {(error || actionError) && <div className="core-history-error" role="alert"><p>{actionError || error}</p>{error && <button type="button" onClick={() => void onRetry()} disabled={loading || locked}>Try again</button>}</div>}
    <p className="core-history-announcement" role="status">{announcement}</p>
    <div aria-busy={loading}>
      {!items.length ? <div className="core-history-empty" role="status">{loading ? <p>Loading chats…</p> : error ? <p>Your saved chats will appear when the connection is restored.</p> : query.trim() ? <><h3>{hasMore ? "No matches on this page" : "No matching chats"}</h3><p>{hasMore ? "Load more chats to keep searching your history." : "Try another word from a title or message."}</p></> : <><h3>Your conversations, saved here.</h3><p>Start a chat in Core, then return here to continue it.</p></>}</div> : <ul className="core-history-list">{items.map(item => <li key={item.id} className={`core-history-row${currentId === item.id ? " is-current" : ""}${editingId === item.id ? " is-editing" : ""}`}>
        {editingId === item.id ? <form className="core-history-rename" onSubmit={event => void renameChat(event, item)}>
          <label htmlFor={`core-chat-title-${item.id}`}>Chat name</label><input id={`core-chat-title-${item.id}`} ref={renameInput} value={draftTitle} onChange={event => { setDraftTitle(event.target.value); setRenameError(null); }} maxLength={120} disabled={locked} aria-invalid={Boolean(renameError)} aria-describedby={renameError ? renameErrorId : undefined}/>
          {renameError && <p id={renameErrorId} role="alert">{renameError}</p>}
          <div><button type="submit" disabled={locked}>{pending?.kind === "rename" ? "Saving…" : "Save name"}</button><button type="button" onClick={stopRename} disabled={locked}>Cancel</button></div>
        </form> : <>
          <button type="button" className="core-history-open" onClick={() => void openChat(item)} disabled={locked || Boolean(editingId)} aria-current={currentId === item.id ? "true" : undefined} aria-label={`Open chat: ${item.title}`}>
            <strong>{item.title || "Untitled chat"}</strong><span className="core-history-preview">{item.preview || "No messages yet"}</span><span className="core-history-meta"><time dateTime={item.updatedAt}>{updatedLabel(item.updatedAt)}</time><span aria-hidden="true">·</span><span>{pending?.id === item.id && pending.kind === "open" ? "Opening…" : stateLabel(item)}</span>{currentId === item.id && <span>Current</span>}</span>
          </button>
          <div className="core-history-actions"><button ref={button => { if (button) renameButtons.current.set(item.id, button); else renameButtons.current.delete(item.id); }} type="button" onClick={() => { setEditingId(item.id); setDraftTitle(item.title); setRenameError(null); setActionError(null); }} disabled={locked || Boolean(editingId)} aria-label={`Rename chat: ${item.title}`}>Rename</button><button type="button" onClick={() => { setDeleteTarget(item); setDeleteError(null); setActionError(null); }} disabled={locked || Boolean(editingId)} aria-label={`Delete chat: ${item.title}`}>Delete</button></div>
        </>}
      </li>)}</ul>}
      {(items.length > 0 || hasMore) && <footer className="core-history-footer">{hasMore && <button type="button" onClick={() => void onLoadMore()} disabled={loading || locked || Boolean(editingId)}>{loading ? "Loading…" : "Load more chats"}</button>}{loading && !hasMore && <p role="status">Updating chats…</p>}</footer>}
    </div>
    {deleteTarget && <div className="core-history-delete-overlay"><button className="core-history-delete-backdrop" type="button" onClick={closeDelete} disabled={pending?.kind === "delete"} aria-label="Keep chat"/><section ref={deletePanel} className="core-history-delete-dialog" role="dialog" aria-modal="true" aria-labelledby={deleteTitleId} aria-describedby={deleteDescriptionId}>
      <h2 id={deleteTitleId}>Delete chat?</h2><p className="core-history-delete-title">{deleteTarget.title}</p><p id={deleteDescriptionId}>This conversation will be removed from your history. This cannot be undone.</p>{deleteError && <p role="alert">{deleteError}</p>}<div><button type="button" onClick={closeDelete} disabled={pending?.kind === "delete"}>Keep chat</button><button type="button" className="primary-button" onClick={() => void removeChat()} disabled={locked}>{pending?.kind === "delete" ? "Deleting…" : "Delete chat"}</button></div>
    </section></div>}
  </section>;
}
